import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Save } from "lucide-react";

import { AppSidebar } from "@/components/sidebar/app-sidebar";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import {
  AppSetting,
  getAppSettings,
  updateAppSetting,
} from "@/services/appSettingsApi";

const ENRICHMENT_KEY = "enrichment_freshness_days";
const MIN_DAYS = 1;
const MAX_DAYS = 730;

function formatMonths(days: number): string {
  const months = days / 30;
  if (!Number.isFinite(months) || days <= 0) return "";
  const rounded = Math.round(months * 10) / 10;
  return `≈ ${rounded} mois`;
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const [setting, setSetting] = useState<AppSetting | null>(null);
  const [value, setValue] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const data = await getAppSettings(navigate);
      const s = data.settings.find((x) => x.key === ENRICHMENT_KEY) ?? null;
      setSetting(s);
      setValue(s?.value_int != null ? String(s.value_int) : "");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const parsed = Number(value);
  const isValid =
    value.trim() !== "" &&
    Number.isInteger(parsed) &&
    parsed >= MIN_DAYS &&
    parsed <= MAX_DAYS;
  const isDirty = setting?.value_int == null || parsed !== setting.value_int;

  const handleSave = async () => {
    if (!isValid) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await updateAppSetting(
        ENRICHMENT_KEY,
        { value_int: parsed },
        navigate,
      );
      setSetting(updated);
      setValue(updated.value_int != null ? String(updated.value_int) : "");
      setSuccess("Réglage enregistré. Propagation aux services sous ~1 min.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <h1 className="text-sm font-medium">Paramètres généraux</h1>
          </div>
        </header>

        <div className="p-6 space-y-4 max-w-2xl">
          <p className="text-sm text-muted-foreground">
            Réglages globaux de la plateforme — identiques pour tous les comptes.
            Modifiables ici par les admins ; la nouvelle valeur est prise en compte
            par les workflows en moins d'une minute.
          </p>

          {error && <div className="text-sm text-destructive">{error}</div>}
          {success && <div className="text-sm text-emerald-600">{success}</div>}

          <Card>
            <CardHeader>
              <CardTitle>Réenrichissement des profils</CardTitle>
              <CardDescription>
                Au démarrage du workflow n8n d'une campagne, un profil candidat
                est réenrichi (re-scrapé sur LinkedIn) si son dernier
                enrichissement est plus ancien que ce délai. Plus la valeur est
                élevée, moins on réenrichit (et moins on sollicite LinkedIn).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Label htmlFor="enrichment-freshness">
                Délai avant réenrichissement (jours)
              </Label>
              <div className="flex items-center gap-3">
                <Input
                  id="enrichment-freshness"
                  type="number"
                  min={MIN_DAYS}
                  max={MAX_DAYS}
                  step={1}
                  className="w-40"
                  value={value}
                  disabled={loading || saving}
                  onChange={(e) => {
                    setValue(e.target.value);
                    setSuccess(null);
                  }}
                />
                <span className="text-sm text-muted-foreground">
                  {isValid ? formatMonths(parsed) : `entre ${MIN_DAYS} et ${MAX_DAYS} jours`}
                </span>
              </div>
              {setting?.updated_at && (
                <p className="text-xs text-muted-foreground">
                  Dernière modification : {new Date(setting.updated_at).toLocaleString("fr-FR")}
                  {setting.updated_by ? ` par ${setting.updated_by}` : ""}
                </p>
              )}
            </CardContent>
            <CardFooter>
              <Button
                onClick={handleSave}
                disabled={loading || saving || !isValid || !isDirty}
              >
                <Save className="mr-2 h-4 w-4" />
                {saving ? "Enregistrement…" : "Enregistrer"}
              </Button>
            </CardFooter>
          </Card>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
